import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BRAND_BLUE = '#4841F0';

const tooltipStyle = {
  borderRadius: '8px',
  border: 'none',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  fontSize: '12px',
  padding: '8px 12px',
};

const ClientMetrics = ({ data }) => {
  const deptTimes = {};
  data.forEach(candidate => {
    const dept = candidate.department || candidate.role || 'Unknown';
    const time = parseInt(candidate.timeToHire) || 0;
    if (candidate.stage === 'Hired' && time > 0) {
      if (!deptTimes[dept]) deptTimes[dept] = { total: 0, count: 0 };
      deptTimes[dept].total += time;
      deptTimes[dept].count += 1;
    }
  });

  const chartData = Object.entries(deptTimes)
    .map(([dept, { total, count }]) => ({
      name: dept.length > 18 ? dept.substring(0, 16) + '...' : dept,
      fullName: dept,
      avgDays: Math.round(total / count),
      hires: count
    }))
    .sort((a, b) => b.avgDays - a.avgDays)
    .slice(0, 8);

  const allTimes = data
    .filter(c => c.stage === 'Hired' && c.timeToHire && parseInt(c.timeToHire) > 0)
    .map(c => parseInt(c.timeToHire));

  const avgOverall = allTimes.length > 0
    ? Math.round(allTimes.reduce((a, b) => a + b, 0) / allTimes.length)
    : 0;
  const fastest = allTimes.length > 0 ? Math.min(...allTimes) : 0;
  const slowest = allTimes.length > 0 ? Math.max(...allTimes) : 0;

  return (
    <div className="dashboard-card p-6 slide-in-up">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Time to Fill</h3>
        <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
          Average Days
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-4">Average days to hire per department for your roles</p>

      {allTimes.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-semibold text-emerald-600">{fastest}d</p>
            <p className="text-xs text-gray-400">Fastest</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-semibold" style={{ color: BRAND_BLUE }}>{avgOverall}d</p>
            <p className="text-xs text-gray-400">Average</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-semibold text-amber-600">{slowest}d</p>
            <p className="text-xs text-gray-400">Slowest</p>
          </div>
        </div>
      )}

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          No time-to-fill data available yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              style={{ fontSize: '12px' }}
              unit="d"
            />
            <YAxis
              type="category"
              dataKey="name"
              width={110}
              tickLine={false}
              axisLine={false}
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name, props) => [
                `${value} days (${props.payload.hires} hire${props.payload.hires !== 1 ? 's' : ''})`,
                'Avg Time to Fill'
              ]}
              labelFormatter={(label) => {
                const item = chartData.find(d => d.name === label);
                return item?.fullName || label;
              }}
            />
            <Bar dataKey="avgDays" fill={BRAND_BLUE} radius={[0, 4, 4, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ClientMetrics;
